import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  ArrowRight, Shield, Award, Users, CheckCircle, 
  Map, PhoneCall, Zap, DollarSign, Headset, Check 
} from "lucide-react";
import BookingModal from "../components/BookingModal";

export default function Home() {
  const navigate = useNavigate();
  const [showLocalModal, setShowModal] = useState(false);

  return (
    <div className="bg-white" data-testid="home-page">
      
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white min-h-[85vh] flex items-center" data-testid="hero-section">
        {/* Background Image with WCAG compliant Slate Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1623051786552-e46ef84e6c07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODI3ODYxODl8MA&ixlib=rb-4.1.0&q=85" 
            alt="Precision Home Leads HQ" 
            className="w-full h-full object-cover object-center scale-105 filter blur-[1px] opacity-40"
          />
          <div className="absolute inset-0 bg-slate-900/75 mix-blend-multiply" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 md:py-32">
          <div className="max-w-3xl space-y-6 animate-in fade-in slide-in-from-bottom-5 duration-700">
            
            {/* Regulatory Trust Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-wider">
              <Shield className="h-3.5 w-3.5" />
              Verified U.S. B2B Marketing Agency
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-none" data-testid="hero-headline">
              Exclusive Homeowner Leads for Growing Home Service Businesses
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl" data-testid="hero-subheadline">
              Precision Home Leads LLC helps contractors connect with qualified homeowners through professional, transparent lead generation and digital marketing services.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button 
                onClick={() => setShowModal(true)}
                className="inline-flex items-center justify-center px-6 py-3.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base transition-colors shadow-lg hover:shadow-emerald-950/20"
                data-testid="hero-book-consultation-btn"
              >
                Book a Consultation
                <ArrowRight className="ml-2.5 h-4 w-4" />
              </button>
              <Link 
                to="/how-it-works"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-semibold text-base transition-colors"
                data-testid="hero-learn-more-btn"
              >
                Learn More
              </Link>
            </div>

            {/* Micro Trust Stats */}
            <div className="grid grid-cols-3 gap-6 pt-12 border-t border-slate-800/60 max-w-lg">
              <div>
                <p className="text-2xl font-bold text-white tracking-tight">100%</p>
                <p className="text-xs text-slate-400">Exclusive Leads</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white tracking-tight">NATIONWIDE</p>
                <p className="text-xs text-slate-400">Coverage Area</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white tracking-tight">SECURE</p>
                <p className="text-xs text-slate-400">Compliance Verified</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Trust & Verification Strip */}
      <section className="bg-slate-50 border-b border-slate-100 py-6">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500 font-medium">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4.5 w-4.5 text-emerald-600" />
            No Long-Term Contracts or Commitments
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4.5 w-4.5 text-emerald-600" />
            TCPA, CCPA & National Underwriting Compliant
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4.5 w-4.5 text-emerald-600" />
            Case-by-Case Invalid Lead Replacements
          </div>
        </div>
      </section>

      {/* Why Contractors Choose Us */}
      <section className="py-20 md:py-28" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16 md:mb-20">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">The Precision Advantage</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="features-headline">
              Why Home Service Contractors Choose Us
            </h2>
            <p className="text-base text-slate-500 leading-relaxed">
              We generate high-quality home services leads with complete transparency. Unlike general listing platforms, we focus on exclusive, pre-qualified opportunities delivered directly to you.
            </p>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Card 1 */}
            <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm transition-all hover:shadow-md hover:border-slate-300" data-testid="feature-card-exclusive">
              <div className="w-12 h-12 rounded bg-slate-50 text-slate-900 flex items-center justify-center mb-6 border border-slate-200">
                <Zap className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Exclusive Homeowner Leads</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Leads are generated specifically for your business in real-time. We never resell the same homeowner inquiry to multiple competing companies.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm transition-all hover:shadow-md hover:border-slate-300" data-testid="feature-card-qualified">
              <div className="w-12 h-12 rounded bg-slate-50 text-slate-900 flex items-center justify-center mb-6 border border-slate-200">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Qualified Inquiries</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Every residential lead is screened to confirm project type, location, and homeowner intent before delivery to guarantee high relevance.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm transition-all hover:shadow-md hover:border-slate-300" data-testid="feature-card-pricing">
              <div className="w-12 h-12 rounded bg-slate-50 text-slate-900 flex items-center justify-center mb-6 border border-slate-200">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Transparent Pricing</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Straightforward pricing options with no hidden fees or administrative markups. Satisfies merchant processor auditing and risk assessments.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm transition-all hover:shadow-md hover:border-slate-300" data-testid="feature-card-support">
              <div className="w-12 h-12 rounded bg-slate-50 text-slate-900 flex items-center justify-center mb-6 border border-slate-200">
                <Headset className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Professional Customer Support</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                A dedicated account manager and robust email support system ready to answer questions, resolve claims, and assist in campaign setups.
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm transition-all hover:shadow-md hover:border-slate-300" data-testid="feature-card-delivery">
              <div className="w-12 h-12 rounded bg-slate-50 text-slate-900 flex items-center justify-center mb-6 border border-slate-200">
                <PhoneCall className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Fast Lead Delivery</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Homeowner details are delivered instantly through email or SMS as soon as the inquiry is validated, ensuring rapid connection.
              </p>
            </div>

            {/* Card 6 */}
            <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm transition-all hover:shadow-md hover:border-slate-300" data-testid="feature-card-coverage">
              <div className="w-12 h-12 rounded bg-slate-50 text-slate-900 flex items-center justify-center mb-6 border border-slate-200">
                <Map className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Nationwide Coverage</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                We generate validated leads in metro and rural communities across all 50 U.S. states, allowing you to grow in any market.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* Image Context Section ( Schweizer High Contrast Style ) */}
      <section className="bg-slate-50 py-16 md:py-24 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">Established Infrastructure</span>
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                Designed to Satisfy Merchant Processor Risk Standards
              </h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                Precision Home Leads LLC is structured to guarantee absolute security, data encryption, and risk compliance. We undergo continuous merchant processor screening (Stripe, primary banks) to ensure strict adherence to refund and transparency disclosures.
              </p>
              
              <ul className="space-y-3.5 text-sm text-slate-600">
                <li className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <Check className="h-3 w-3" />
                  </div>
                  Secure, SSL Encrypted Lead Transport
                </li>
                <li className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <Check className="h-3 w-3" />
                  </div>
                  Documented Case-by-Case Refund / Lead Replacement
                </li>
                <li className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <Check className="h-3 w-3" />
                  </div>
                  Verified Real-Time Direct Contact Channels
                </li>
              </ul>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-lg overflow-hidden border border-slate-200 shadow-md">
                <img 
                  src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4Mjc4NjE5MHww&ixlib=rb-4.1.0&q=85"
                  alt="Corporate Infrastructure" 
                  className="w-full h-auto object-cover object-center max-h-[400px]"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Industries We Serve Teaser Grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16">
            <div className="space-y-4 max-w-2xl">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">Our Services</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                Premium Leads Across 11 Key Industries
              </h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                We generate targeted consumer queries for a diverse range of home renovation and maintenance trades. Click to learn how we capture leads for your specific niche.
              </p>
            </div>
            <Link 
              to="/industries" 
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded border border-slate-200 bg-white text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors"
              data-testid="industries-view-all-btn"
            >
              View All Industries
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {["Painting", "Concrete", "Tree Removal", "Junk Removal"].map((ind, i) => (
              <div 
                key={ind} 
                onClick={() => navigate(`/industries?focus=${ind}`)}
                className="bg-white border border-slate-200 rounded p-6 hover:shadow-md cursor-pointer transition-all hover:border-slate-300"
                data-testid={`industry-teaser-${ind.toLowerCase().replace(' ', '-')}`}
              >
                <h4 className="font-semibold text-slate-900 text-base mb-1">{ind}</h4>
                <p className="text-xs text-slate-500 mb-4">Residential lead acquisition campaigns.</p>
                <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1 group-hover:underline">
                  Target Leads <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Call to Action Strategy Banner */}
      <section className="bg-slate-900 text-white py-16 md:py-24 relative overflow-hidden" data-testid="cta-banner">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10 text-center space-y-6 max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Ready to Connect with Exclusive Homeowners?
          </h2>
          <p className="text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl mx-auto">
            Schedule a brief 1-on-1 growth strategy session with our team. We&apos;ll analyze your geographic target market and provide a custom lead flow model tailored to your capacity.
          </p>
          <div className="pt-4 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center justify-center px-6 py-3 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors shadow-lg"
              data-testid="cta-book-call-btn"
            >
              Book My Consultation
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 rounded bg-transparent border border-slate-700 text-white font-semibold text-sm hover:bg-slate-800 transition-colors"
              data-testid="cta-contact-btn"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </section>

      {/* Booking Modal (GoHighLevel Calendar) */}
      <BookingModal open={showLocalModal} onClose={() => setShowModal(false)} />

    </div>
  );
}
